export type UserFacingErrorContext =
  | "conversation"
  | "microphone"
  | "scoring"
  | "survey"
  | "login";

export interface UserFacingError {
  title: string;
  description: string;
}

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`.trim();
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const value = error as { code?: unknown; message?: unknown; status?: unknown };
    return [value.code, value.status, value.message]
      .filter((part) => part !== undefined && part !== null)
      .map(String)
      .join(" ");
  }
  return String(error ?? "");
}

const DEFAULTS: Record<UserFacingErrorContext, UserFacingError> = {
  conversation: {
    title: "患者の回答を受け取れませんでした",
    description: "少し待ってから、もう一度マイクを押して話してください。",
  },
  microphone: {
    title: "マイクを開始できませんでした",
    description: "マイクの設定を確認して、もう一度マイクを押してください。",
  },
  scoring: {
    title: "採点を開始できませんでした",
    description: "少し待ってから、もう一度「採点開始」を押してください。",
  },
  survey: {
    title: "アンケートを保存できませんでした",
    description: "通信を確認し、少し待ってからもう一度「送信」を押してください。",
  },
  login: {
    title: "ログインできませんでした",
    description: "入力内容と通信を確認して、もう一度ログインしてください。",
  },
};

export function toUserFacingError(
  error: unknown,
  context: UserFacingErrorContext,
): UserFacingError {
  const text = errorText(error).toLowerCase();

  if (context === "scoring" && /empty_interview|まだ発話がありません/.test(text)) {
    return {
      title: "まだ面接内容がありません",
      description: "マイクで話して患者の回答を受け取ってから、「採点開始」を押してください。",
    };
  }

  if (/\b429\b|too many requests|rate.?limit/.test(text)) {
    const retryAction: Record<UserFacingErrorContext, string> = {
      conversation: "もう一度マイクを押してください。",
      microphone: "もう一度マイクを押してください。",
      scoring: "もう一度「採点開始」を押してください。",
      survey: "もう一度「送信」を押してください。",
      login: "もう一度ログインしてください。",
    };
    return {
      title: "ただいま混み合っています",
      description: `10秒ほど待ってから、${retryAction[context]}`,
    };
  }

  if (/notallowederror|permission denied|permission dismissed|microphone permission/.test(text)) {
    return {
      title: "マイクの使用が許可されていません",
      description: "ブラウザの設定でマイクを許可してから、もう一度マイクを押してください。",
    };
  }

  if (/notfounderror|requested device not found|no audio input/.test(text)) {
    return {
      title: "マイクが見つかりません",
      description: "端末のマイク設定を確認してから、もう一度お試しください。",
    };
  }

  if (/timeout|timed out|etimedout/.test(text)) {
    return {
      title: "処理に時間がかかっています",
      description: context === "scoring"
        ? "少し待ってから、もう一度「採点開始」を押してください。"
        : "通信を確認し、少し待ってからもう一度お試しください。",
    };
  }

  if (/failed to fetch|load failed|networkerror|network error|econnreset|websocket/.test(text)) {
    return {
      title: "通信を確認できませんでした",
      description: "接続が戻るまで少し待ってから、もう一度お試しください。",
    };
  }

  return DEFAULTS[context];
}
